import { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  User, 
  Shield, 
  Bell, 
  Moon, 
  Globe, 
  HelpCircle, 
  FileText,
  ChevronRight,
  Check
} from 'lucide-react'
import { useStore } from '@/store'
import { toast } from 'sonner'

interface SettingItemProps {
  icon: React.ElementType
  label: string
  value?: string
  onClick?: () => void
  toggle?: boolean
  toggleValue?: boolean
  onToggle?: () => void
}

function SettingItem({ 
  icon: Icon, 
  label, 
  value, 
  onClick,
  toggle,
  toggleValue,
  onToggle
}: SettingItemProps) {
  return (
    <button
      onClick={toggle ? onToggle : onClick}
      className="w-full flex items-center justify-between p-4 rounded-2xl bg-background-elevated hover:bg-background-hover transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-background flex items-center justify-center">
          <Icon className="w-5 h-5 text-text-secondary" />
        </div>
        <span className="font-medium">{label}</span>
      </div>
      
      <div className="flex items-center gap-2">
        {value && <span className="text-text-secondary">{value}</span>}
        {toggle ? (
          <div className={`w-12 h-6 rounded-full transition-colors relative ${
            toggleValue ? 'bg-brand' : 'bg-border'
          }`}>
            <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${
              toggleValue ? 'left-7' : 'left-1'
            }`} />
          </div>
        ) : (
          <ChevronRight className="w-5 h-5 text-text-tertiary" />
        )}
      </div>
    </button>
  )
}

export default function Settings() {
  const user = useStore(state => state.user)
  const [darkMode, setDarkMode] = useState(true)
  const [notifications, setNotifications] = useState(true)
  const [biometrics, setBiometrics] = useState(false)
  
  const handleKYC = () => {
    toast.info('KYC verification coming soon!')
  }
  
  const handleSecurity = () => {
    toast.info('Security settings coming soon!')
  }
  
  const handleHelp = () => {
    toast.info('Help center coming soon!')
  }
  
  const handleTerms = () => {
    toast.info('Terms of service coming soon!')
  }
  
  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <h1 className="text-2xl font-bold">Settings</h1>
      
      {/* Profile Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-6 rounded-3xl bg-gradient-card border border-border"
      >
        <div className="flex items-center gap-4">
          {user?.avatar ? (
            <img 
              src={user.avatar} 
              alt={user.firstName}
              className="w-16 h-16 rounded-2xl"
            />
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-brand/20 flex items-center justify-center">
              <User className="w-8 h-8 text-brand" />
            </div>
          )}
          <div>
            <h2 className="text-xl font-semibold">
              {user?.firstName} {user?.lastName}
            </h2>
            <p className="text-text-secondary">{user?.email}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                user?.kycStatus === 'approved' 
                  ? 'bg-accent-green/20 text-accent-green' 
                  : 'bg-accent-orange/20 text-accent-orange'
              }`}>
                {user?.kycStatus === 'approved' && <Check className="w-3 h-3" />}
                {user?.kycStatus === 'approved' ? 'Verified' : 'Pending KYC'}
              </span>
            </div>
          </div>
        </div>
      </motion.div>
      
      {/* Account Section */}
      <section>
        <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-3 ml-1">
          Account
        </h3>
        <div className="space-y-2">
          <SettingItem 
            icon={User} 
            label="Personal Information"
            onClick={() => toast.info('Coming soon!')}
          />
          <SettingItem 
            icon={Shield} 
            label="KYC Verification"
            value={user?.kycStatus === 'approved' ? 'Approved' : 'Pending'}
            onClick={handleKYC}
          />
          <SettingItem 
            icon={Shield} 
            label="Security"
            onClick={handleSecurity}
          />
        </div>
      </section>
      
      {/* Preferences Section */}
      <section>
        <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-3 ml-1">
          Preferences
        </h3>
        <div className="space-y-2">
          <SettingItem 
            icon={Moon} 
            label="Dark Mode"
            toggle
            toggleValue={darkMode}
            onToggle={() => setDarkMode(!darkMode)}
          />
          <SettingItem 
            icon={Bell} 
            label="Notifications"
            toggle
            toggleValue={notifications}
            onToggle={() => setNotifications(!notifications)}
          />
          <SettingItem 
            icon={Globe} 
            label="Language"
            value="English"
            onClick={() => toast.info('Language settings coming soon!')}
          />
        </div>
      </section>
      
      {/* Security Section */}
      <section>
        <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-3 ml-1">
          Security
        </h3>
        <div className="space-y-2">
          <SettingItem 
            icon={Shield} 
            label="Biometric Authentication"
            toggle
            toggleValue={biometrics}
            onToggle={() => setBiometrics(!biometrics)}
          />
          <SettingItem 
            icon={Shield} 
            label="Two-Factor Authentication"
            value="Enabled"
            onClick={() => toast.info('2FA settings coming soon!')}
          />
        </div>
      </section>
      
      {/* Support Section */}
      <section>
        <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-3 ml-1">
          Support
        </h3>
        <div className="space-y-2">
          <SettingItem 
            icon={HelpCircle} 
            label="Help Center"
            onClick={handleHelp}
          />
          <SettingItem 
            icon={FileText} 
            label="Terms of Service"
            onClick={handleTerms}
          />
        </div>
      </section>
      
      {/* App Version */}
      <div className="text-center pt-4">
        <p className="text-text-tertiary text-sm">Capybank v1.0.0</p>
        <p className="text-text-muted text-xs mt-1">© 2024 Capybank. All rights reserved.</p>
      </div>
    </div>
  )
}